import useVuelidate from '@vuelidate/core';
import { TypeCampaignEnum } from '@/types';
import {
  requiredWithMessage,
  segmentationCompleteValidator,
  triggerSegmentationVisitValidator,
} from '@/helpers/CustomValidator';
import type { FlowOperatorMetadata, ValidationResult } from '@/types/flow-types';
import { canDeleteTrigger } from '@/components/flows/operators/registry';
import { predefinedEvents } from '@/composables/events/constants';
import {
  hasTriggerSegmentation,
  isSegmentableTriggerEvent,
  parseTriggerSegmentation,
  savedTriggerSegmentation,
} from '@/composables/segmentations/triggerSegmentation';
import Form from './Form.vue';

export interface VisitPageFilters {
  products: Record<string, any>;
  categories: Record<string, any>;
  manufacturers: Record<string, any>;
  urls: Record<string, any>;
}

export interface TriggerInternalEventData {
  event: string;
  webhook: number;
  // Sub-form data depending on selected event
  multiplebox_type?: string; // QuitSite
  percent_scroll?: number; // ScrollPage
  time_unit_select?: { unit: string; value: number }; // SitePassTime
  multiplebox_global_session?: string; // SitePassTime
  custom_title?: string; // VisitPage
  custom_description?: string; // VisitPage
  trigger_location?: number; // VisitPage
  // VisitPage filters
  products?: Record<string, any>;
  categories?: Record<string, any>;
  manufacturers?: Record<string, any>;
  urls?: Record<string, any>;
  // Custom persistence for events with persistant flag
  customPersistance?: Record<string, string[]>;
  // CustomJS token script
  token_script?: string; // CustomJS
  // VisitPage and CustomJS: segmentation evaluated when the page loads
  segmentation_config?: any[];
}

export function createDefaultData(data: Partial<TriggerInternalEventData> = {}): TriggerInternalEventData {
  return {
    event: data.event ?? '',
    webhook: data.webhook ?? 0,
    multiplebox_type: data.multiplebox_type,
    percent_scroll: data.percent_scroll,
    time_unit_select: data.time_unit_select,
    multiplebox_global_session: data.multiplebox_global_session,
    custom_title: data.custom_title,
    custom_description: data.custom_description,
    trigger_location: data.trigger_location,
    token_script: data.token_script,
    segmentation_config: data.segmentation_config,
  };
}

const TriggerInternalEvent: FlowOperatorMetadata<TriggerInternalEventData> = {
  id: 'boxtriggerinternalevent',
  kind: 'trigger',
  label: 'flows.operators.triggerInternalEvent.label',
  icon: 'fa-bolt',
  hasConfiguration: true,
  availableIn: [TypeCampaignEnum.AUTOMATION],

  incompatibilities: {
    [TypeCampaignEnum.AUTOMATION]: {
      nextOperators: [
        'boxsitepasstime', 'boxquitsite',
        'boxperiodiclaunching', 'boxscrollpage', 'boxstatuscommand',
        'boxcustomjs', 'boxregisteronsite', 'boxsubscribetonewsletter',
        'boxvisitpage', 'boxtriggerinternalevent',
      ],
    },
  },

  Form,

  defaultData: createDefaultData,

  getDisplayLabel: (data: TriggerInternalEventData, t: (key: string, params?: any) => string) => {
    // For visit_page, use custom_title if set by user
    if (data.event === 'visit_page' && data.custom_title) {
      return data.custom_title;
    }

    // Generate dynamic labels based on event type and data
    switch (data.event) {
      case 'pass_time':
        return t('automatedScenarios.triggers.passTime.shortLabel');

      case 'scroll':
        return t('automatedScenarios.triggers.scrollPage.shortLabel');

      case 'quit_site':
        if (data.multiplebox_type === '0') {
          return t('automatedScenarios.fields.hasQuit');
        }
        return t('automatedScenarios.fields.isGoingToQuit');

      case 'abandon_cart':
        return t('automatedScenarios.triggers.abandonPanier.label');

      case 'upsert_order':
        return t('automatedScenarios.triggers.statusCommand.label');

      case 'custom_js':
        return t('automatedScenarios.triggers.customJS.label');

      default:
        // For other predefined events, use their label
        if (data.event && predefinedEvents[data.event]) {
          return t(predefinedEvents[data.event].label);
        }
        // For custom events
        if (data.event) {
          return `${t('automatedScenarios.triggers.triggerInternalEvent.label')} : ${data.event}`;
        }
        return null;
    }
  },

  getSubtitle: (data: TriggerInternalEventData, t: (key: string, params?: any) => string) => {
    switch (data.event) {
      case 'pass_time':
        if (data.time_unit_select) {
          const timeUnitSelect = typeof data.time_unit_select === 'string' 
            ? JSON.parse(data.time_unit_select) 
            : data.time_unit_select;
          const unitKey = timeUnitSelect.unit.toLowerCase();
          const plural = timeUnitSelect.value > 1 ? 's' : '';
          const unit = t(`automatedScenarios.fields.durations.${unitKey}${plural}`);
          return `${timeUnitSelect.value} ${unit}`;
        }
        return null;

      case 'scroll':
        if (data.percent_scroll !== undefined) {
          return `${data.percent_scroll}%`;
        }
        return null;

      default:
        if (isSegmentableTriggerEvent(data.event) && hasTriggerSegmentation(data.segmentation_config)) {
          return t('flows.operators.triggerInternalEvent.segmentation.subtitle');
        }
        return null;
    }
  },

  outputs: () => 1,

  async validate(data: TriggerInternalEventData): Promise<ValidationResult> {
    const rules: any = {
      event: {
        requiredWithMessage: requiredWithMessage(),
      },
    };

    // Add validation rules based on selected event
    if (data.event === 'quit_site') {
      rules.multiplebox_type = {
        requiredWithMessage: requiredWithMessage(),
      };
    } else if (data.event === 'scroll') {
      rules.percent_scroll = {
        requiredWithMessage: requiredWithMessage(),
      };
    } else if (data.event === 'pass_time') {
      rules.time_unit_select = {
        requiredWithMessage: requiredWithMessage(),
      };
      rules.multiplebox_global_session = {
        requiredWithMessage: requiredWithMessage(),
      };
    } else if (data.event === 'visit_page') {
      rules.custom_title = {
        requiredWithMessage: requiredWithMessage(),
      };
    }

    // Segmentation of visit_page / custom_js, checked once a condition is set
    const isSegmented = isSegmentableTriggerEvent(data.event) && hasTriggerSegmentation(data.segmentation_config);
    if (isSegmented) {
      rules.segmentation_config = {
        segmentationCompleteValidator: segmentationCompleteValidator(),
        triggerSegmentationVisitValidator: triggerSegmentationVisitValidator(),
      };
    }

    const v$ = useVuelidate(rules, isSegmented
      ? { ...data, segmentation_config: parseTriggerSegmentation(data.segmentation_config) }
      : data);
    const success = await v$.value.$validate();
    return {
      success,
      validate: v$.value,
    };
  },

  async callback(data: TriggerInternalEventData): Promise<TriggerInternalEventData> {
    // Define fields allowed for each event type
    const fieldsByEvent: Record<string, string[]> = {
      quit_site: ['multiplebox_type'],
      scroll: ['percent_scroll'],
      pass_time: ['time_unit_select', 'multiplebox_global_session'],
      visit_page: ['custom_title', 'custom_description', 'trigger_location', 'products', 'categories', 'manufacturers', 'urls'],
      custom_js: ['token_script'],
    };

    // Common fields always preserved
    const commonFields = ['event', 'webhook', 'custom_title'];

    // Get allowed fields for current event
    const currentEventFields = fieldsByEvent[data.event] || [];
    const allowedFields = new Set([...commonFields, ...currentEventFields]);

    // Collect all known specific fields to ensure cleanup
    const allSpecificFields = new Set<string>();
    Object.values(fieldsByEvent).forEach(fields => fields.forEach(f => allSpecificFields.add(f)));

    // Construct clean object
    const cleanedData: any = {};

    // 1. Copy allowed fields
    allowedFields.forEach((field) => {
      if (field in data) {
        cleanedData[field] = data[field as keyof TriggerInternalEventData];
      }
    });

    // 2. custom_title is only used for visit_page (user-defined title)
    // For other events, getDisplayLabel generates the label dynamically

    // 3. Handle custom persistence for persistant events (matching old system behavior)
    if (data.event && predefinedEvents[data.event]?.persistant) {
      cleanedData.customPersistance = {
        boxtriggerinternalevent: ['{CURRENT_OPERATOR_ID}'],
      };
    } else {
      // Explicitly clear customPersistance for non-persistant events
      cleanedData.customPersistance = undefined;
    }

    // 4. Segmentation of visit_page / custom_js. Once the panel has held a
    // configuration, undefined removes the one saved before; a trigger whose
    // panel stayed empty keeps exactly its former keys.
    const segmentation = savedTriggerSegmentation(data.event, data.segmentation_config);
    if (segmentation || parseTriggerSegmentation(data.segmentation_config).length > 0) {
      cleanedData.segmentation_config = segmentation;
    }

    return cleanedData;
  },

  canBeDeleted(operatorId: string, flow: any): Promise<boolean | string> {
    return canDeleteTrigger(flow, operatorId);
  },
};

export default TriggerInternalEvent;
